<template>
  <div class="brand-article">
    <div class="article-content">
      <div class="content-container">
        <p class="date">Le {{ dateToString(page.date) }}</p>
        <h2 class="title">{{ page.title }}</h2>
        <hr />
        <div
          class="content"
          v-html="youtubeFacadeHtml(optimizeContentImages(page.content1))"
        />
        <div
          v-if="page.cta1?.text"
          class="cta"
        >
          <p class="text">{{ page.cta1.text }}</p>
          <a
            class="button-action"
            :href="page.cta1.route"
            target="_blank"
          >
            {{ page.cta1.labelButton }}
          </a>
        </div>
      </div>
      <div
        v-if="page.fullImage"
        class="full-image"
      >
        <img
          :src="page.fullImage.url"
          :alt="page.title"
          loading="lazy"
        />
      </div>
      <div class="content-container">
        <div
          v-if="page.content2"
          class="content"
          v-html="youtubeFacadeHtml(optimizeContentImages(page.content2))"
        />
        <div
          v-if="page.cta2?.text"
          class="cta"
        >
          <p class="text">{{ page.cta2.text }}</p>
          <a
            class="button-action"
            :href="page.cta2.route"
            target="_blank"
          >
            {{ page.cta2.labelButton }}
          </a>
        </div>
      </div>
    </div>

    <slot name="middle-container"></slot>

    <div class="bottom-container">
      <div
        class="social-container"
        :class="{ full: page.hide_comments === true }"
      >
        <div class="social">
          <BrandSocial :page="page" />
        </div>
      </div>
      <div
        class="side-container"
        :class="{ full: page.hide_comments === true }"
      >
        <div class="side">
          <BrandSide
            v-if="page.sideContent?.community || page.sideContent?.card"
            :community="page.sideContent.community"
            :card="page.sideContent.card"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { FormattedArticle } from '~/types'

const props = defineProps<{
  page: FormattedArticle
}>()

useVideoSchema(() => ({
  ids: [
    ...new Set([
      ...youtubeIdsInHtml(props.page.content1),
      ...youtubeIdsInHtml(props.page.content2),
    ]),
  ],
  name: props.page.name || props.page.title,
  description: props.page.title || props.page.content1,
  uploadDate: props.page.date,
}))
</script>

<style lang="scss" scoped>
.brand-article {
  margin-top: 3.8em;
  margin-right: auto;
  margin-left: auto;

  .article-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    max-width: 1600px;
    margin-right: auto;
    margin-left: auto;

    .content-container {
      width: 100%;
      max-width: 103rem;
      padding: 0 8rem;

      hr {
        width: 100%;
      }

      .date {
        margin-bottom: 1.2rem;
        font-size: 1rem;
        font-weight: 500;
        letter-spacing: 0.1rem;
        text-transform: uppercase;
        opacity: 0.5;
      }

      .title {
        margin-bottom: 4.2rem;
        font-size: 3rem;
        font-weight: 400;
        letter-spacing: 0;
        text-transform: none;
      }

      .content {
        margin-top: 4rem;

        img {
          width: 100%;
          height: auto;
          margin-top: 4rem;
          margin-bottom: 4rem;
        }
      }

      .cta {
        display: flex;
        justify-content: space-between;
        align-items: center;
        height: 10.7rem;
        margin-top: 4rem;
        border-top: 1px solid rgba($black, 0.2);
        border-bottom: 1px solid rgba($black, 0.2);

        .label {
          font-weight: 500;
        }

        .button {
          flex-shrink: 0;
        }
      }
    }

    .full-image {
      width: 100%;
      max-height: 53.3rem;
      margin-top: 4rem;
      overflow: hidden;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }
  }

  .bottom-container {
    display: flex;
    margin-top: 8rem;

    .social-container {
      flex-grow: 1;
      padding-bottom: 4rem;
    }

    .side-container {
      background: $sideGrey;
    }

    .side {
      flex-shrink: 0;
      width: 49.7rem;
      height: 100%;
    }
  }
}

@media screen and (min-width: 2000px) {
  .brand-article {
    .bottom-container {
      .social-container {
        display: flex;
        flex-grow: 0;
        justify-content: flex-end;
        width: calc(50vw - 100rem + 150.5rem);

        .social {
          width: 150.5rem;
        }
      }

      .side-container {
        display: flex;
        justify-content: flex-start;
        width: calc(50vw - 100rem + 49.5rem);
        background: $sideGrey;

        .side-item {
          width: 49.5rem;
        }
      }
    }
  }
}

@media screen and (max-width: 1024px) {
  .brand-article {
    .bottom-container {
      flex-wrap: wrap;

      .social {
        padding-bottom: 4rem;
      }

      .side {
        width: 100%;
      }
    }
  }
}

@media screen and (max-width: 700px) {
  .brand-article {
    .article-content {
      .content-container {
        padding: 0 2.5rem;

        .cta {
          height: auto;
          padding: 2rem 0;

          .text {
            margin-right: 2rem;
          }
        }
      }
    }

    .bottom-container {
      margin-top: 5rem;
    }
  }
}

@media screen and (max-width: 430px) {
  .brand-article {
    .article-content {
      .content-container {
        .cta {
          flex-direction: column;

          .text {
            margin-right: 0;
            margin-bottom: 2rem;
          }
        }
      }
    }

    .full {
      width: 100%;
    }
  }
}
</style>

<style lang="scss">
.brand-article {
  .article-content {
    p {
      margin-bottom: 2rem;
    }

    img:not(.yt-facade__thumb) {
      display: block;
      width: 100% !important;
      height: auto !important;
      margin-bottom: 4rem;
    }

    iframe,
    .yt-facade {
      display: block;
      width: 100%;
      height: 40rem;
      margin-bottom: 4rem;
    }

    a:not(.button-action) {
      color: $blue;
    }

    .button {
      color: $white;
    }

    ol {
      padding: 0 2rem 2rem;

      li p {
        margin-bottom: 0;
      }
    }

    ul {
      padding: 0 2rem 2rem;
      list-style: disc;

      li p {
        margin-bottom: 0;
      }
    }
  }
}
</style>
